import { LinearGradient } from 'expo-linear-gradient';
import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';

import { useDeletePerson } from '@/features/api/hooks/shared-data-privacy/useDeletePerson';
import { usePopup } from '@/features/popup/provider/usePopup';
import { SUPPORT_URL } from '@/features/profile-setting/constants/links';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { StateView } from '@/shared/components/state-view/StateView';
import { handleOpenUrl } from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  gradient: { flex: 1 },
  wrapper: {
    paddingHorizontal: 12,
    paddingVertical: 32,
    gap: 32
  },
  content: {
    gap: 16,
    flexDirection: 'column',
    alignItems: 'flex-start'
  }
});

export const DeleteAccountScreen: FC = () => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PRIVACY, {
    keyPrefix: 'screens.delete-account'
  });

  const { showAlert } = usePopup();
  const { mutate: deletePerson, isError, isPending } = useDeletePerson();

  const handleDeletePerson = useCallback(
    () =>
      showAlert({
        dismissable: true,
        confirmColor: theme.colors.error,
        labels: t('alerts.confirm-delete', { returnObjects: true }),
        onConfirm: () => deletePerson()
      }),
    [deletePerson, showAlert, t, theme.colors.error]
  );

  return (
    <LinearGradient
      style={styles.gradient}
      colors={[
        theme.colors.surfaceContainer,
        theme.colors.secondaryContainer,
        theme.colors.secondaryContainer
      ]}
    >
      <StateView
        isLoading={isPending}
        isError={isError}
        contentContainerStyle={styles.wrapper}
        backgroundColor="transparent"
      >
        <View style={styles.content}>
          <Text variant="headlineSmall">{t('help.title')}</Text>
          <Text variant="bodyLarge">{t('help.content')}</Text>
          <Button
            mode="contained"
            onPress={() => {
              handleOpenUrl(SUPPORT_URL);
            }}
          >
            {t('help.button')}
          </Button>
        </View>
        <View style={styles.content}>
          <Text variant="headlineSmall">{t('delete.title')}</Text>
          <Text variant="bodyLarge">{t('delete.content')}</Text>
          <Button
            mode="contained"
            onPress={handleDeletePerson}
            buttonColor={theme.colors.error}
          >
            {t('delete.button')}
          </Button>
        </View>
      </StateView>
    </LinearGradient>
  );
};
